import * as React from 'react';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  CloseEmptyIcon,
} from '@align-cpf/uikit-icons';
import { ContextualMenu, Icon, InputText, Typography } from '../../atoms';
import { IDropdownProps, dropdownLocators } from '../Dropdown';
import { IOptionProps } from '../Option';
import { useDropDown } from '../Dropdown/Dropdown.hooks';
import clsx from 'clsx';
import useComboBoxStyles from './Combobox.styles';
import useStyles from '../Dropdown/Dropdown.styles';
export interface IComboboxProps<T>
  extends Omit<IDropdownProps<T, true>, 'allowClear'> {
  /**
   * Default string displayed when list of options is empty
   */
  noOptionsText?: string;
  /**
   *  Function to compare options with current combobox's input value
   */
  compareFunction?(
    childrenProps: React.ReactElement<unknown, string>['props'],
    inputValue: string,
  ): boolean;
}

const { locators } = dropdownLocators();

function isOptionProperties<T>(
  props: IOptionProps<T> | unknown,
): props is IOptionProps<T> {
  return (props as IOptionProps<T>).label !== undefined;
}

function defaultComparer<T>(
  childrenProps: React.ReactElement<T, string>['props'],
  inputValue: string,
) {
  if (isOptionProperties(childrenProps)) {
    return (childrenProps.label as string)
      .toLowerCase()
      .includes(inputValue.toLowerCase());
  }
  return false;
}

const Combobox = <T,>({
  onChange,
  compareFunction = defaultComparer,
  ...props
}: React.PropsWithChildren<IComboboxProps<T>>) => {
  const classes = useStyles();
  const comboBoxClasses = useComboBoxStyles();
  const [inputValue, setInputValue] = React.useState('');
  const {
    name,
    placeholder = 'Choose an option',
    variant = 'white',
    disabled,
    listPosition = 'bottom',
    autoPosition = false,
    children,
    noOptionsText = 'No Options',
    ...rest
  } = props;

  const {
    selectedItem,
    handleClear,
    toggleDropdown,
    isOpen,
    options,
    setIsOpen,
  } = useDropDown(
    { ...props, onChange, children, allowClear: true },
    {
      handleClearCallback: () => setInputValue(''),
      handleClickCallback: () => setInputValue(''),
    },
  );
  const filteredOptions = inputValue
    ? options?.filter((option) => {
        return compareFunction(option.props, inputValue);
      })
    : options;

  const handleInputChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (isOpen === false) {
        setIsOpen(true);
      }
      setInputValue(e.target.value);
    },
    [setIsOpen, setInputValue, isOpen],
  );

  return (
    <div className={classes.root}>
      <ContextualMenu
        isOpen={isOpen}
        position={listPosition}
        onClickOutside={toggleDropdown}
        autoPosition={autoPosition}
        triggerComponent={
          <div className={classes.trigger}>
            <InputText
              {...rest}
              name={name}
              placeholder={placeholder}
              readOnly={Boolean(selectedItem)}
              value={selectedItem?.label || inputValue}
              onChange={handleInputChange}
              onClick={toggleDropdown}
              variant={variant}
              _UNSAFE_classname={clsx({
                [classes.input]: true,
                [comboBoxClasses.input]: true,
                [classes.inputActive]: isOpen,
                [classes.clearable]: selectedItem || inputValue,
              })}
              disabled={disabled}
              autoComplete={'off'}
            />
            <div
              {...locators.iconContainer({ disabled })}
              className={clsx({
                [classes.iconContainer]: true,
                [classes.iconDisabled]: disabled,
              })}
            >
              {selectedItem || inputValue ? (
                <div onClick={handleClear}>
                  <Icon
                    icon={CloseEmptyIcon}
                    className={classes.clear}
                    locatorsOverrides={locators.iconClear()}
                  />
                </div>
              ) : null}
              <div
                onClick={toggleDropdown}
                className={clsx({ [classes.invert]: listPosition === 'top' })}
              >
                {isOpen ? (
                  <Icon
                    icon={ChevronUpIcon}
                    className={classes.chevron}
                    locatorsOverrides={locators.iconClose()}
                  />
                ) : (
                  <Icon
                    icon={ChevronDownIcon}
                    className={classes.chevron}
                    locatorsOverrides={locators.iconOpen()}
                  />
                )}
              </div>
            </div>
          </div>
        }
      >
        {filteredOptions && filteredOptions.length ? (
          filteredOptions
        ) : (
          <Typography
            className={comboBoxClasses.noOptions}
            variant="body01"
            as="div"
          >
            {noOptionsText}
          </Typography>
        )}
      </ContextualMenu>
    </div>
  );
};

export default Combobox;
